const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

export interface AuthUser {
  email: string;
  name: string;
  picture?: string;
}

export interface AuthState {
  user: AuthUser | null;
  allowed: boolean;
  admin: boolean;
  /** Files per batch for this account. `null` means no limit. */
  fileLimit?: number | null;
}

export interface AllowedUser {
  email: string;
  addedAt: number;
  addedBy: string;
  /** Absent means the server default; `0` means no limit. */
  fileLimit?: number;
}

/** Thrown with the status so the sign-in screen can tell "refused" from "broken". */
export class AuthRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly email?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(BASE + path, { credentials: 'include', ...init });
  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = body as { error?: string; email?: string } | null;
    throw new AuthRequestError(
      detail?.error || 'Request failed (' + response.status + ')',
      response.status,
      detail?.email,
    );
  }
  return body as T;
}

function json(method: string, body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
}

export function getAuthConfig(): Promise<{ googleClientId: string; configured: boolean }> {
  return request('/api/auth/config');
}

export function getSession(): Promise<AuthState> {
  return request('/api/auth/me');
}

export function signInWithGoogle(credential: string): Promise<AuthState> {
  return request('/api/auth/google', json('POST', { credential }));
}

export function signOut(): Promise<{ ok: boolean }> {
  return request('/api/auth/signout', { method: 'POST' });
}

export function listAllowedUsers(): Promise<{ users: AllowedUser[]; owner: string; defaultLimit: number }> {
  return request('/api/auth/users');
}

export function addAllowedUser(email: string): Promise<{ user: AllowedUser }> {
  return request('/api/auth/users', json('POST', { email }));
}

/** `null` grants an unlimited batch size; `undefined` restores the default. */
export function setUserLimit(email: string, fileLimit: number | null | undefined): Promise<{ user: AllowedUser }> {
  return request('/api/auth/users/' + encodeURIComponent(email), json('PATCH', { fileLimit }));
}

export function removeAllowedUser(email: string): Promise<{ ok: boolean }> {
  return request('/api/auth/users/' + encodeURIComponent(email), { method: 'DELETE' });
}
